/* eslint-disable max-len */
/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Injectable
} from '@angular/core';
import {
	DynamicComponentLookup
} from '@dynamicComponents/dynamic-component.lookup';
import {
	EntityInstanceComponent
} from '@entity/components/entity-instance/entity-instance.component';
import {
	AddDynamicPrintMenuAction
} from '@operation/actions/add-dynamic-print-menu-action';
import {
	AddRelatedContextMenuAction
} from '@operation/actions/add-related-context-menu-action';
import {
	IOperationAction
} from '@operation/actions/interfaces/operation-action.interface';
import {
	OperationFactory
} from '@operation/factories/operation-factory';
import {
	IOperationDefinition
} from '@operation/interfaces/operation-definition.interface';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	DocumentHelper
} from '@shared/helpers/document.helper';
import {
	EventHelper
} from '@shared/helpers/event.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IApplicationMessage
} from '@shared/interfaces/application-messages/application-message.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	MenuItem
} from 'primeng/api';

/**
 * A class to handle executing all operation definition
 * based logic.
 *
 * @export
 * @class OperationExecutionService
 */
@Injectable()
export class OperationExecutionService
{
	/**
	 * Creates an instance of an OperationExecutionService.
	 *
	 * @param {ActivityService} activityService
	 * The factivity service used to execute this operation.
	 * @param {OperationFactory} operationFactory
	 * The factory used to create operation actions dynamically.
	 * @memberof OperationExecutionService
	 */
	public constructor(
		public activityService: ActivityService,
		private readonly operationFactory: OperationFactory)
	{
	}

	/**
	 * Gets the lookup value to use when looking up an associated entity
	 * type in a related context menu configuration.
	 *
	 * @type {string}
	 * @memberof OperationExecutionService
	 */
	private readonly associatedEntityTypeIdentifier: string =
		'associatedEntityTypeGroup';

	/**
	 * Executes a fully mapped operation definition populated via
	 * the operation service.
	 *
	 * @async
	 * @param {IOperationDefinition} operationDefinition
	 * The operation definition to execute.
	 * @returns {Promise<IOperationAction>} The operation action
	 * that was executed.
	 * @memberof OperationExecutionService
	 */
	public async executeMappedOperation(
		operationDefinition: IOperationDefinition): Promise<IOperationAction>
	{
		try
		{
			const operationAction: IOperationAction =
				this.operationFactory.create(
					operationDefinition.operationType.name,
					operationDefinition.operationTypeParameters);

			operationAction.label = operationDefinition.label;
			operationAction.icon = operationDefinition.icon;
			operationAction.order = operationDefinition.order;
			operationAction.pageContext = operationDefinition.pageContext;
			let data: any = null;

			if (operationAction.canDeactivateOperation === false)
			{
				return null;
			}

			// block interface.
			if (!AnyHelper.isNullOrEmpty(operationAction.blockInterface))
			{
				DocumentHelper.setElementDisplay(
					AppConstants.cssClasses.entityLayoutMask,
					<boolean>operationAction.blockInterface);
			}

			if (AnyHelper.isNullOrEmpty(
				operationAction.operationSuccessMessage))
			{
				await operationAction.execute();
			}
			else
			{
				data = await this.activityService.handleActivity(
					new Activity(
						operationAction.execute(),
						'<strong>Calling Action</strong> '
							+ operationAction.operationName,
						'<strong>Completed Action</strong> '
							+ operationAction.operationName,
						operationAction.operationSuccessMessage,
						operationAction.operationFailureMessage),
					AppConstants.activityStatus.info);
			}

			// Handle execute action errors.
			if (typeof data === AppConstants.variableTypes.string)
			{
				let messages: IApplicationMessage[] = [];
				try
				{
					const jsonData = JSON.parse(data);
					messages =
						AnyHelper.isNull(jsonData.messages)
							? []
							: jsonData.messages;
				}
				catch (exception)
				{
					// More cases may need handled here as they arise.
				}

				if (messages.length === 0
					|| messages.filter(
						(message: IApplicationMessage) =>
							message.status ===
								AppConstants.activityStatus.error).length === 0)
				{
					await operationAction.executePostOperationActions();
				}
			}
			else
			{
				await operationAction.executePostOperationActions();
			}

			// unblock interface.
			this.handleUnblockingInterface(operationAction);

			return operationAction;
		}
		catch (error)
		{
			EventHelper.dispatchBannerEvent(
				AppConstants.messages.genericErrorMessage,
				error.message,
				AppConstants.activityStatus.error,
				error);
		}

		return null;
	}

	/**
	 * Populates and returns a list of Menu Items by executing
	 * an action associated to the operation if the passed in definition
	 * is dynamic, else return empty array. Will also set the
	 * dynamic flag on the definition based on the icon.
	 *
	 * @async
	 * @param {IOperationDefinition} operationDefinition
	 * The operation definition to execute the action from.
	 * @param {IDynamicComponentContext<Component, any>} pageContext
	 * The page context to be used in populating the menu items.
	 * @param {boolean} enabled
	 * A boolean indicating if the dynamic operation is enabled.
	 * @param {boolean} visible
	 * A boolean indicating if the dynamic operation is visible.
	 * @param {string} disabledMessage
	 * The disabled message string to be set in the menu item.
	 * @returns {Promise<MenuItem[]>} The a list of menu items
	 * that were returned by the action executed.
	 * @memberof OperationExecutionService
	 */
	public async populateDynamicOperations(
		operationDefinition: IOperationDefinition,
		pageContext: IDynamicComponentContext<
			Component, any>,
		enabled: boolean,
		visible: boolean,
		disabledMessage?: string): Promise<MenuItem[]>
	{
		operationDefinition.dynamic = true;
		switch(operationDefinition.icon)
		{
			case AppConstants.contextMenuIdentifiers
				.addDynamicPrintMenuIdentifier:
			{
				const operationAction: AddDynamicPrintMenuAction =
					<AddDynamicPrintMenuAction>
						await this.executeMappedOperation(
							operationDefinition);

				return operationAction.execute();
			}
			case AppConstants.contextMenuIdentifiers
				.addRelatedContextMenuIdentifier:
			{
				if (visible === false)
				{
					return [];
				}

				const operationAction: AddRelatedContextMenuAction =
					<AddRelatedContextMenuAction>
						await this.executeMappedOperation(
							operationDefinition);

				const isNested: boolean =
					operationAction.singularEndpoints.indexOf(
						operationAction.endPoint) === -1;

				const contextMenuRelationships: MenuItem[] =
					enabled === false && isNested === true
						? null
						: await operationAction.execute();

				if (isNested === false
					&& !AnyHelper.isNullOrEmptyArray(
						contextMenuRelationships))
				{
					contextMenuRelationships[0].disabled = !enabled;
				}

				// Do not display the relation if singular
				// and no results.
				if (isNested === true
					|| contextMenuRelationships.length > 0)
				{
					return isNested === true
						? [<MenuItem>
							{
								label: StringHelper.interpolate(
									operationDefinition.label,
									pageContext),
								icon: enabled === false
									? null
									: operationDefinition.icon,
								id: operationAction[
									// eslint-disable-next-line max-len
									this.associatedEntityTypeIdentifier],
								disabled: !enabled,
								visible: visible,
								tooltipOptions: {
									tooltipLabel: disabledMessage
								},
								items: contextMenuRelationships
							}]
						: contextMenuRelationships;
				}
				break;
			}
			default:
			{
				operationDefinition.dynamic = false;

				return [];
			}
		}

		// If we broke out of the switch statement
		// then return an empty array.

		return [];
	}

	/**
	 * Handles unblocking the interface after an operation action
	 * has been executed.
	 *
	 * @param {IOperationAction} operationAction
	 * The operation action to unblock the interface for.
	 * @memberof OperationExecutionService
	 */
	private handleUnblockingInterface(
		operationAction: IOperationAction): void
	{
		if (AnyHelper.isNullOrEmpty(operationAction.unblockInterface))
		{
			return;
		}

		const unblockInterface: boolean =
			!operationAction.unblockInterface;
		DocumentHelper.setElementDisplay(
			AppConstants.cssClasses.entityLayoutMask,
			unblockInterface);

		// Refresh the history for an entity instance component.
		if (unblockInterface === true
			&& operationAction.pageContext.source
				instanceof EntityInstanceComponent)
		{
			setTimeout(
				() =>
				{
					EventHelper.dispatchRefreshComponentEvent(
						DynamicComponentLookup.supportedTypes
							.historyComponent,
						DynamicComponentLookup.targetComponents
							.utilityMenuComponent);
				},
				AppConstants.time.oneThirdSecond);
		}
	}
}